import React, { useState } from "react";
import { errorText } from "@/lib/api";

/**
 * Pasul „alege fișierul și apasă Importă" — alegerea fișierului NU pornește importul; abia butonul „Importă" îl pornește.
 * `onImport(file, onProgress)` trebuie să arunce eroarea la eșec: mesajul exact al serverului apare sub buton (nu doar „Eroare import").
 * Dacă pasează `onProgress` către `postImport`, sub buton apare bara de progres (încărcare, apoi rânduri procesate și timp rămas).
 */
export default function ImportFileStep({ accept = ".xlsx", onImport, testId, hint = "Selectează fișierul .xlsx completat", compact }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [prog, setProg] = useState(null);

  const run = async () => {
    if (!file || busy) return;
    setBusy(true); setError(""); setProg(null);
    try { await onImport(file, setProg); }
    catch (e) { setError(errorText(e, "Importul a eșuat")); }
    finally { setBusy(false); setProg(null); }
  };

  return (
    <div style={{ marginTop: compact ? 8 : 16, padding: compact ? 0 : 16, border: compact ? "none" : "2px dashed #93C5FD", borderRadius: 8, textAlign: compact ? "left" : "center" }}>
      <input type="file" accept={accept} data-testid={testId}
        onChange={e => { setFile(e.target.files?.[0] || null); setError(""); }} />
      <div style={{ fontSize: 12, color: "#64748B", marginTop: 8 }}>
        {file ? <>Fișier ales: <b>{file.name}</b> ({Math.max(1, Math.round(file.size / 1024))} KB) — apasă „Importă” ca să înceapă importul.</> : hint}
      </div>
      <div style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-primary" disabled={!file || busy} onClick={run} data-testid={testId ? `${testId}-go` : undefined}>
          {busy ? "Se importă…" : "Importă"}
        </button>
      </div>
      {busy && prog && <ProgressBar prog={prog} testId={testId ? `${testId}-progress` : undefined} />}
      {error && (
        <div role="alert" data-testid={testId ? `${testId}-error` : undefined}
          style={{ marginTop: 12, padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8,
                   color: "#991B1B", fontSize: 13, textAlign: "left", whiteSpace: "pre-wrap" }}>
          <b>Importul nu s-a făcut.</b> {error}
        </div>
      )}
    </div>
  );
}

const PHASE = { reading: "Se citește fișierul", writing: "Se importă", finalizing: "Se finalizează importul", finished: "Gata" };
const fmt = (sec) => (sec < 90 ? `${Math.max(1, Math.round(sec))} s` : `${Math.round(sec / 60)} min`);

/** Bara de progres a unui import: încărcarea fișierului, apoi rândurile procesate din total, cu timp rămas estimat. */
function ProgressBar({ prog, testId }) {
  const upload = prog.stage === "upload";
  const total = prog.total || 0, done = prog.done || 0;
  const pct = upload ? prog.percent : (total ? Math.min(99, Math.round(done * 100 / total)) : null);
  let label, eta = "";
  if (upload) label = prog.percent >= 100 ? "Fișier încărcat. Serverul îl pregătește…" : `Se încarcă fișierul… ${prog.percent}%`;
  else if (prog.phase === "writing" && total) {
    label = `Se importă: rândul ${done} din ${total} (${pct}%)`;
    if (done > 0 && prog.elapsed_s > 2) eta = ` · aproximativ ${fmt(prog.elapsed_s * (total - done) / done)} rămase`;
  } else label = `${PHASE[prog.phase] || "Se procesează"}…`;
  return (
    <div data-testid={testId} style={{ marginTop: 14, textAlign: "left" }} role="status" aria-live="polite">
      <div style={{ fontSize: 13, color: "var(--navy-3)", fontWeight: 600, marginBottom: 6 }}>{label}{eta}</div>
      <div style={{ height: 10, background: "#E2E8F0", borderRadius: 99, overflow: "hidden" }}>
        <div data-testid={testId ? `${testId}-bar` : undefined}
          style={{ height: "100%", width: pct == null ? "35%" : `${pct}%`, borderRadius: 99,
                   background: "linear-gradient(90deg, var(--navy), var(--blue-primary))", transition: "width .4s ease",
                   animation: pct == null ? "ef-progress-slide 1.2s ease-in-out infinite alternate" : "none" }} />
      </div>
      <div style={{ fontSize: 11, color: "#64748B", marginTop: 6 }}>Importul rulează. Nu închide fereastra până se termină.</div>
      <style>{"@keyframes ef-progress-slide { from { margin-left: 0 } to { margin-left: 65% } }"}</style>
    </div>
  );
}
